import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { products, catalogFilters, styleFilters } from "@/data/site";
import { useLeadModal } from "@/context/LeadModalContext";
import { trackViewContent } from "@/lib/meta";

const matches = (p, filter) => {
  if (filter === "Tutti") return true;
  if (filter === "New") return p.isNew;
  return p.cat === filter;
};

const matchesStyle = (p, style) => style === "Tutti gli stili" || p.style === style;

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const Catalog = () => {
  const [filter, setFilter] = useState("Tutti");
  const [style, setStyle] = useState("Tutti gli stili");
  const { openModal } = useLeadModal();
  const visible = products.filter((p) => matches(p, filter) && matchesStyle(p, style));

  const openProduct = (p) => {
    trackViewContent(p);
    openModal();
  };

  return (
    <section id="catalogo" className="bg-ivory py-24 sm:py-36" data-testid="catalog">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-sans-ui text-[11px] tracking-mega text-ink/40 mb-6">CAPITOLO 03 — IL CATALOGO</p>
        </Reveal>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <Reveal delay={0.05}>
            <h2 className="font-display font-light text-ink text-4xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight max-w-2xl">
              Migliaia di capi.<br />Una sola identità: la tua.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-sans-ui text-sm text-ink/60 max-w-xs lg:text-right">
              Costruisci una collezione che sembri nata interamente per il tuo brand.
            </p>
          </Reveal>
        </div>

        {/* Filters */}
        <Reveal delay={0.1}>
          <div className="flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar lg:flex-wrap lg:overflow-visible pb-2 mb-10 border-b hairline">
            {catalogFilters.map((f) => (
              <button
                key={f}
                data-testid={`filter-btn-${slug(f)}`}
                onClick={() => setFilter(f)}
                className={`whitespace-nowrap font-sans-ui text-[11px] tracking-mega uppercase px-3 py-3 transition-colors duration-300 ${
                  filter === f ? "text-ink border-b-2 border-ink -mb-[2px]" : "text-ink/45 hover:text-ink"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Style filter */}
        <Reveal delay={0.1}>
          <div className="-mt-4 mb-10 flex flex-wrap items-center gap-2" data-testid="style-filters">
            <span className="font-sans-ui text-[10px] tracking-mega text-ink/40 mr-2">STILE</span>
            {styleFilters.map((st) => (
              <button
                key={st}
                data-testid={`style-btn-${slug(st)}`}
                onClick={() => setStyle(st)}
                className={`font-sans-ui text-[10px] tracking-mega uppercase px-3 py-2 border transition-colors duration-300 ${
                  style === st ? "bg-ink text-ivory border-ink" : "hairline text-ink/55 hover:border-ink hover:text-ink"
                }`}
              >
                {st}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Grid */}
        <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.div
                key={p.id}
                layout
                data-testid={`product-card-${p.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.04 }}
                className="group cursor-pointer"
                onClick={() => openProduct(p)}
              >
                <div className="relative overflow-hidden bg-linen aspect-[3/4]">
                  <img
                    src={p.img}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[900ms] ease-out"
                  />
                  <span className="absolute top-3 left-3 bg-ivory/95 backdrop-blur px-2.5 py-1.5 font-sans-ui text-[9px] tracking-wide text-ink/70">
                    {p.badge}
                  </span>
                  {p.isNew && (
                    <span className="absolute top-3 right-3 bg-ink text-ivory px-2 py-1 font-sans-ui text-[9px] tracking-mega">NEW</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="font-sans-ui text-[13px] text-ink">{p.name} <span className="text-ink/35 text-[10px] tracking-wide ml-1">{p.code}</span></p>
                  <span className="font-sans-ui text-[10px] tracking-wide uppercase text-ink/40">{p.cat}</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {visible.length === 0 && (
          <p data-testid="catalog-empty" className="py-16 text-center font-sans-ui text-sm text-ink/45">
            Nessun capo dimostrativo con questa combinazione. Il catalogo completo è disponibile su richiesta.
          </p>
        )}

        <Reveal delay={0.05}>
          <p data-testid="catalog-disclaimer" className="mt-10 font-sans-ui text-[12px] text-ink/45 italic">
            Una selezione dimostrativa del catalogo. Il catalogo completo è disponibile su richiesta.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t hairline pt-10">
            <p className="font-display italic text-2xl sm:text-3xl text-ink">Il catalogo è nostro. La selezione diventa tua.</p>
            <button
              data-testid="catalog-cta"
              onClick={openModal}
              className="inline-flex items-center bg-ink text-ivory px-8 py-4 text-[11px] tracking-mega font-medium hover:bg-charcoal transition-colors duration-300"
            >
              RICHIEDI ACCESSO
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
